import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'

import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/common/ui/dialog'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { Button } from '@/common/ui/button'
import useMutationAttachDomain from '../../api/hooks/useMutationAttachDomain'

const formSchema = z.object({
	subdomain: z
		.string()
		.min(1, 'กรุณากรอกซับโดเมน')
		.regex(
			/^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?$/,
			'ซับโดเมนต้องประกอบด้วยตัวอักษร ตัวเลข หรือ - เท่านั้น',
		),
})

type FormValues = z.infer<typeof formSchema>

type AttachDomainDialogProps = {
	clientDomain: string
}

export default function AttachDomainDialog({
	clientDomain,
}: AttachDomainDialogProps) {
	const [open, setOpen] = useState(false)
	const { mutate: attachDomain, isPending } = useMutationAttachDomain()

	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			subdomain: '',
		},
	})

	const onSubmit = (values: FormValues) => {
		const fullDomain = `${values.subdomain}.${clientDomain}`
		attachDomain(
			{ domain: fullDomain },
			{
				onSuccess: () => {
					setOpen(false)
					form.reset()
				},
			},
		)
	}

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button>เพิ่มโดเมน</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-[500px]">
				<DialogHeader>
					<DialogTitle>เพิ่มโดเมน</DialogTitle>
					<DialogDescription>
						กรอกซับโดเมนที่ต้องการเพื่อเชื่อมต่อกับระบบ
					</DialogDescription>
				</DialogHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
						<FormField
							control={form.control}
							name="subdomain"
							render={({ field }) => (
								<FormItem>
									<FormLabel>ซับโดเมน</FormLabel>
									<FormControl>
										<div className="flex items-center">
											<Input
												placeholder="member"
												{...field}
												disabled={isPending}
												className="rounded-r-none"
											/>
											<span className="flex h-10 items-center rounded-r-md border border-l-0 border-input bg-muted px-3 text-sm text-muted-foreground">
												.{clientDomain}
											</span>
										</div>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => {
									setOpen(false)
									form.reset()
								}}
								disabled={isPending}
							>
								ยกเลิก
							</Button>
							<Button type="submit" disabled={isPending}>
								{isPending ? 'กำลังเพิ่ม...' : 'เพิ่ม'}
							</Button>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	)
}
